import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import "react-phone-number-input/style.css";
import { Message } from "primereact/message";
import PhoneInput from "react-phone-number-input";
import { Toast } from "primereact/toast";
import { paymentLinkService } from "../../../services/paymentLinkService";
import { useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Loader } from "../../common/LoaderPaymentLink";

const PaymentLink: React.FC = () => {
  const [selected, setSelected] = useState<string | null>(null);
  const toastRef = useRef<Toast>(null);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const { id_link } = useParams<{ id_link: string }>();
  const [fetchError, setFetchError] = useState<string | null>(null);

  const handleSelect = (operator: { code: string; id: string }) => {
    setSelected(operator.code);
    formik.setFieldValue("serviceId", operator.id);

    formik.setFieldValue("selected", operator.code);
    formik.setTouched({ ...formik.touched, selected: true });
    
  };

  const fetchPaymentLinks = async () => {
    if (!id_link) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Payment ID is missing",
        life: 3000,
      });
      return;
    }
    try {
      const res = await paymentLinkService.initPayment(id_link);
      if(res.status === -1){
         toastRef.current?.show({
        severity: "warn",
        summary: "Operation failed",
        detail: res.message,
        life: 3000,
      });
      setFetchError("Link expired or invalid");
      return;

      }
      setPaymentData(res.data);
     
    } catch (error) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch payment data",
        life: 3000,
      });
    }
  };

 const formik = useFormik({
  initialValues: {
    serviceId: "",
    phoneNumber: "",
    selected: "",
  },
  validationSchema: Yup.object({
    serviceId: Yup.string().required("Service is required"),
    phoneNumber: Yup.string().required("Phone number is required"),
    selected: Yup.string().required("Please select a payment method"),
  }),
  onSubmit: async (values) => {
    setSubmitting(true);
    setIsCheckingStatus(true);

    try {
      const initPayment = await paymentLinkService.initPaymentLinkTransaction({
        serviceId: values.serviceId,
        phoneNumber: values.phoneNumber,
        linkId: id_link!,
      });

      if (initPayment.status === -1) {
        toastRef.current?.show({
          severity: "warn",
          summary: "Transaction failed",
          detail: initPayment.message,
          life: 3000,
        });
        setSubmitting(false);
        setIsCheckingStatus(false);
        return;
      }

      const statusResponse = await paymentLinkService.PaymentLinkCheckStatus(id_link!);

      if (statusResponse.status === -1) {
        toastRef.current?.show({
          severity: "warn",
          summary: "Status Check Failed",
          detail: statusResponse.message,
          life: 3000,
        });
        setSubmitting(false);
        setIsCheckingStatus(false);
        return;
      }

      const currentStatus = statusResponse.data.transaction.status;

      if (currentStatus === "SUCCESS") {
        toastRef.current?.show({
          severity: "success",
          summary: "Payment Successful",
          detail: statusResponse.message,
          life: 3000,
        });
        setSubmitting(false);
        setIsCheckingStatus(false);
        return;
      }

      toastRef.current?.show({
        severity: "info",
        summary: "Payment Processing",
        detail: "Waiting for final confirmation...",
        life: 4000,
      });

      setTimeout(async () => {
        try {
          const finalStatus = await paymentLinkService.PaymentLinkCheckStatus(id_link!);
          const transactionStatus = finalStatus.data.transaction.status;

          if (transactionStatus === "FAILED") {
            toastRef.current?.show({
              severity: "warn",
              summary: "Transaction Failed",
              detail: finalStatus.message,
              life: 3000,
            });
          } else if (transactionStatus === "SUCCESS") {
            toastRef.current?.show({
              severity: "success",
              summary: "Payment Successful",
              detail: finalStatus.message,
              life: 3000,
            });
          } else {
            toastRef.current?.show({
              severity: "info",
              summary: "Still Pending",
              detail: "Transaction is still pending.",
              life: 3000,
            });
          }
        } catch (error) {
          toastRef.current?.show({
            severity: "error",
            summary: "Status Check Failed",
            detail: "Unable to retrieve final payment status.",
            life: 4000,
          });
        } finally {
          setSubmitting(false);
          setIsCheckingStatus(false);
        }
      }, 300000);

    } catch (error) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to initiate payment",
        life: 3000,
      });
      setSubmitting(false);
      setIsCheckingStatus(false);
    }
  },
});


  useEffect(() => {
    fetchPaymentLinks();
  }, []);

  if (fetchError) {
  return (
    <div className="flex flex-col justify-center items-center h-screen text-center">
      <Toast ref={toastRef} className="toast-top-center" />
      <p className="text-red-500 text-lg mb-4">{fetchError}</p>
      <Button label="Retry" onClick={fetchPaymentLinks} icon="pi pi-refresh" />
    </div>
  );
}

if (!paymentData) {
  return <Loader message="Loading payment link..." />;
}


  

  return (
    <div className="h-screen flex justify-center items-center mt-4">
      <Toast ref={toastRef} className="toast-top-center" /> {isCheckingStatus && <Loader />}
        <div className="flex flex-col sm:flex-row justify-between gap-5">
          <div className="bg-white rounded-lg shadow-lg w-96 p-6 text-center">
            <p className="mb-2">
              Dear <strong>{paymentData.merchantName}</strong> : <p className="text-sm">There are your payment information to proceed to payment</p>
            </p>
            <div className="payment-info">
              <table className="mb-6 w-full border border-gray-300">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border border-gray-300 p-2" colSpan={2}>
                      Payment information
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border border-gray-300">
                    <td className="border border-gray-300 p-2">Amount</td>
                    <td className="border border-gray-300 p-2">{paymentData.amount} {paymentData.currency}</td>
                  </tr>
                  <tr className="border border-gray-300">
                    <td className="border border-gray-300 p-2">Total to pay</td>
                    <td className="border border-gray-300 p-2">{paymentData.amount} {paymentData.currency}</td>
                  </tr>
                  <tr className="border border-gray-300">
                    <td className="border border-gray-300 p-2">Reason</td>
                    <td className="border border-gray-300 p-2">{paymentData.reason}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg w-96 p-6 text-center">
            <p className="mb-2">Select your payment method to proceed to payment</p>
            <form onSubmit={formik.handleSubmit}>
              <div className="payment-method">
                <div className="grid grid-cols-3 gap-4">
                  {paymentData.services.map((operator: any, index: number) => (
                    <div
                      key={index}
                      className={`w-24 h-24 border-4 rounded-md cursor-pointer overflow-hidden flex justify-center items-center transition-all ${
                        selected === operator.code ? "border-blue-600 shadow-lg" : "border-gray-600"
                      }`}
                      onClick={() => handleSelect(operator)}
                    >
                      <img
                        src={`/assets/images/services/${operator.code}.png`}
                        alt={operator.name}
                        className="object-cover w-full h-full"
                      />
                    </div>
                  ))}
                  
                </div>
                  {formik.errors.selected && formik.touched.selected && (
                  <Message severity="error" text={formik.errors.selected} className="input-message" />
                   )}
              </div>
              <div className="field mt-3 col-span-2 sm:grid-cols-2 md:grid-cols-3">
                <PhoneInput
                  placeholder="Enter phone number"
                  international
                  id="phoneNumber"
                  name="phoneNumber"
                  defaultCountry={paymentData.customerCountry}
                  countries={[paymentData.customerCountry]}
                  className={`w-full p-2 border rounded-md ${
                    formik.errors.phoneNumber && formik.touched.phoneNumber
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  value={formik.values.phoneNumber}
                  onBlur={formik.handleBlur}
                  onChange={(phoneNumber) => formik.setFieldValue("phoneNumber", phoneNumber)}
                />
                {formik.errors.phoneNumber && formik.touched.phoneNumber && (
                  <Message severity="error" text={formik.errors.phoneNumber} className="input-message" />
                )}
              </div>
              {!submitting && (
                  <Button
                    type="submit"
                    label="Submit"
                    loading={submitting}
                    disabled={submitting}
                    icon="pi pi-pay"
                    className="w-1/2 bg-primary-600 text-white py-2 mt-2 
                    rounded-md flex items-center justify-center gap-2 
                    hover:bg-primary-700 transition-all mx-auto"
                  />
                )}
            </form>
          </div>
        </div>
    </div>
  );
};

export default PaymentLink;